import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const inputClass =
  'w-full bg-transparent border-b-2 border-forest/20 focus:border-terracotta focus:outline-none py-3 text-forest placeholder-forest/40 transition-colors';
const labelClass = 'block text-xs tracking-[0.2em] uppercase text-forest/70 mb-2';

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Default landing after sign-in is the user dashboard. If they were
  // redirected here from a protected page, send them back there instead.
  const from = (location.state as { from?: string } | null)?.from || '/dashboard';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!email || !password) {
      setError('Email and password are required.');
      return;
    }
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      navigate(from, { replace: true });
    } else {
      setError(res.error);
    }
  }

  return (
    <div className="max-w-md mx-auto px-6 lg:px-12 py-20">
      <div className="text-xs tracking-[0.25em] uppercase text-terracotta mb-3">Sign in</div>
      <h1
        className="text-4xl text-forest mb-3 leading-tight font-display"
        style={{ fontVariationSettings: '"opsz" 144' }}
      >
        Welcome back.
      </h1>
      <p className="text-forest/70 mb-10 leading-relaxed">
        Sign in to your MowKind beta account.
      </p>

      <form onSubmit={onSubmit} className="space-y-8">
        <div>
          <label className={labelClass}>Email</label>
          <input
            type="email"
            className={inputClass}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            autoFocus
          />
        </div>
        <div>
          <label className={labelClass}>Password</label>
          <input
            type="password"
            className={inputClass}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            autoComplete="current-password"
          />
        </div>

        {error && (
          <div className="bg-terracotta/10 border-l-4 border-terracotta p-4 text-terracotta text-sm">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="bg-terracotta text-cream w-full px-8 py-4 text-sm tracking-[0.1em] uppercase hover:bg-forest transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Signing in…' : 'Sign in →'}
        </button>

        <p className="text-sm text-forest/60 text-center pt-4">
          No account yet?{' '}
          <Link to="/signup" className="text-terracotta hover:underline">
            Join the beta
          </Link>
        </p>
      </form>
    </div>
  );
}
